/**
 * Hands vertical drags on a snap-point sheet's content to the SHEET whenever
 * nothing under the finger can natively scroll that way.
 *
 * vaul drags on pointer events, and the moment the browser claims a touch as a
 * native pan it fires `pointercancel` — vaul hears nothing after that, and has
 * no `pointercancel` handler of its own. It then either drops the drag, or its
 * `pointerout` fallback releases on the two or three moves it did see and
 * flings the sheet to the next snap point on its own. That is the "fight".
 *
 * The browser claims far more pans than the ones that can actually scroll:
 * - the sheet's scroller at `scrollTop` 0 still latches a pull-down, to
 *   rubber-band it;
 * - any `overflow-*: auto` element is a scroll container, and a scroll
 *   container resets `touch-action` — so vaul's `touch-action: none` on the
 *   sheet does not reach the sticky tab bar or a slot rail inside it, even
 *   when they do not overflow. A drag that starts on the tabs never moved the
 *   sheet, in either direction.
 *
 * Which of those the finger happened to land on is why it only fought
 * sometimes, and why lifting, tapping and dragging again seemed to fix it.
 *
 * Cancelling the touchmoves of a vertical drag that cannot scroll anything
 * keeps the browser out of the gesture, so vaul receives every move and the
 * sheet follows the finger. Everything else stays native: scrolling content
 * that can scroll, horizontal swipes (rails, carousels), and multi-touch.
 */

/**
 * Movement before the direction is decided. Under the browser's own pan slop
 * (~10px on iOS, more on Chrome), so the decision lands before a native pan
 * can begin.
 */
const DECIDE_PX = 6;

/**
 * Whether anything from `target` up to and including `sheet` can natively
 * scroll for a finger moving `fingerDy` (positive = finger moving down, i.e.
 * scrolling content back up).
 *
 * The pull-down test is vaul's own `shouldDrag` test verbatim
 * (`scrollHeight > clientHeight && scrollTop !== 0`, which also covers iOS's
 * negative overscroll bounce) so the two never disagree: if this cancelled a
 * touch that vaul then refused, the gesture would do nothing at all.
 */
function canScrollNatively(
  target: EventTarget | null,
  sheet: HTMLElement,
  fingerDy: number,
) {
  let node = target instanceof Element ? target : null;
  while (node) {
    if (node instanceof HTMLElement && node.scrollHeight > node.clientHeight) {
      if (fingerDy > 0) {
        if (node.scrollTop !== 0) return true;
      } else {
        const { overflowY } = getComputedStyle(node);
        if (
          (overflowY === "auto" || overflowY === "scroll") &&
          node.scrollTop + node.clientHeight < node.scrollHeight - 1
        ) {
          return true;
        }
      }
    }
    if (node === sheet) return false;
    node = node.parentElement;
  }
  return false;
}

export function attachSheetPullHandoff(sheet: HTMLElement): () => void {
  let startX = 0;
  let startY = 0;
  let target: EventTarget | null = null;
  let mode: "native" | "undecided" | "sheet" = "native";

  const onTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    if (!touch || event.touches.length !== 1) {
      mode = "native";
      return;
    }
    startX = touch.clientX;
    startY = touch.clientY;
    target = event.target;
    mode = "undecided";
  };

  const onTouchMove = (event: TouchEvent) => {
    if (mode === "native") return;
    const touch = event.touches[0];
    if (!touch || event.touches.length !== 1) {
      mode = "native";
      return;
    }
    if (mode === "undecided") {
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < DECIDE_PX) return;
      const vertical = Math.abs(dy) > Math.abs(dx);
      mode =
        vertical && !canScrollNatively(target, sheet, dy) ? "sheet" : "native";
      if (mode === "native") return;
    }
    // Once the sheet owns the gesture it keeps it, reversals included — vaul
    // is tracking the finger in both directions from here.
    if (event.cancelable) event.preventDefault();
  };

  const onTouchEnd = () => {
    mode = "native";
    target = null;
  };

  sheet.addEventListener("touchstart", onTouchStart, { passive: true });
  // Must be non-passive: this is the listener that cancels.
  sheet.addEventListener("touchmove", onTouchMove, { passive: false });
  sheet.addEventListener("touchend", onTouchEnd, { passive: true });
  sheet.addEventListener("touchcancel", onTouchEnd, { passive: true });

  return () => {
    sheet.removeEventListener("touchstart", onTouchStart);
    sheet.removeEventListener("touchmove", onTouchMove);
    sheet.removeEventListener("touchend", onTouchEnd);
    sheet.removeEventListener("touchcancel", onTouchEnd);
  };
}
